import { VdpTemplate, CSVData } from '../types';

/**
 * Auto-creates and synchronizes CSV columns & sample record rows for all dynamic elements in a VDP template
 * (such as QR codes, barcodes, serial numbers, text fields, prices, dates, etc.)
 */
export function autoSyncCsvForTemplate(
  template: VdpTemplate,
  existingCsv: CSVData | null
): CSVData {
  if (!template || !template.elements) {
    return existingCsv || { headers: [], rows: [] };
  }

  // Get all unique dynamic element bindings (columnName)
  const dynamicElements = template.elements.filter(
    el => !el.isStatic && el.columnName && el.columnName.trim() !== ''
  );

  const templateColumns = Array.from(
    new Set(dynamicElements.map(el => el.columnName!.trim()))
  );

  if (templateColumns.length === 0) {
    return existingCsv || { headers: [], rows: [] };
  }

  // Determine standard number of rows (default to 10 if none exist)
  const rowCount = existingCsv && existingCsv.rows && existingCsv.rows.length > 0 
    ? existingCsv.rows.length 
    : 10;

  const existingHeaders = existingCsv?.headers || [];
  const mergedHeaders = Array.from(new Set([...existingHeaders, ...templateColumns]));

  const newRows = Array.from({ length: rowCount }).map((_, rowIndex) => {
    const rowObj: Record<string, any> = existingCsv?.rows?.[rowIndex] 
      ? { ...existingCsv.rows[rowIndex] } 
      : {};

    templateColumns.forEach(colName => {
      // Only generate if row does not already have data for this column
      if (rowObj[colName] === undefined || rowObj[colName] === null || rowObj[colName] === '') {
        const matchingEl = dynamicElements.find(e => e.columnName?.trim() === colName);
        rowObj[colName] = generateSampleValueForElement(colName, matchingEl, rowIndex);
      }
    });

    return rowObj;
  });

  return {
    headers: mergedHeaders,
    rows: newRows
  };
}

export function generateSampleValueForElement(
  colName: string,
  element: any | undefined,
  rowIndex: number
): string {
  const num = rowIndex + 1;
  const colLower = colName.toLowerCase();
  const type = element?.type || '';

  // 1. QR Code / URLs / Gate Codes
  if (type === 'qr' || colLower.includes('qr') || colLower.includes('url') || colLower.includes('track') || colLower.includes('link')) {
    if (element?.staticValue && (element.staticValue.startsWith('http') || element.staticValue.includes('/'))) {
      return `${element.staticValue.replace(/\d+$/, '')}${1000 + num}`;
    }
    return `https://vdp.app/track/${colLower.replace(/[^a-z0-9]/g, '-')}-${1000 + num}`;
  }

  // 2. Barcodes / EAN-13 / DataMatrix / UPCA
  if (type === 'barcode' || type === 'ean13' || type === 'upca' || type === 'datamatrix' || colLower.includes('barcode') || colLower.includes('ean') || colLower.includes('code')) {
    if (type === 'ean13' || colLower.includes('ean')) {
      const base12 = (890100000000 + num).toString().slice(0, 12);
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += parseInt(base12[i]) * (i % 2 === 0 ? 1 : 3);
      }
      const checksum = (10 - (sum % 10)) % 10;
      return base12 + checksum;
    }
    return `890${(100000000 + num * 7).toString().slice(-9)}`;
  }

  // 3. Serial Numbers / IDs / Passes
  if (type === 'serial' || colLower.includes('serial') || colLower.includes('sn') || colLower.includes('id') || colLower.includes('pass')) {
    const prefix = element?.prefix ? element.prefix.trim() : (colName.toUpperCase().slice(0, 3) + '-');
    return `${prefix}${num.toString().padStart(5, '0')}`;
  }

  // 4. Currency / Prices
  if (type === 'currency' || colLower.includes('price') || colLower.includes('cost') || colLower.includes('sale') || colLower.includes('amount')) {
    return (19.99 + (rowIndex * 12.50)).toFixed(2);
  }

  // 5. Dates
  if (type === 'date' || colLower.includes('date') || colLower.includes('mfg') || colLower.includes('exp')) {
    const today = new Date();
    today.setDate(today.getDate() + rowIndex);
    return today.toISOString().split('T')[0];
  }

  // 6. Text / Names / Titles
  if (element?.staticValue && element.staticValue !== `Sample ${type}`) {
    return `${element.staticValue} #${num}`;
  }

  // General fallbacks based on column name hints
  if (colLower.includes('name') || colLower.includes('title') || colLower.includes('item') || colLower.includes('product') || colLower.includes('asset')) {
    const samples = [
      'Premium Wireless Headphones',
      'MacBook Pro M3 Max',
      'Organic Honey 500g',
      'Dr. Sarah Jenkins',
      'Ultra HD Smart Monitor',
      'Ergonomic Desk Chair',
      'Stainless Water Bottle',
      'Bluetooth Speaker Mini',
      'Mechanical Gaming Keyboard',
      'Noise Cancelling Earbuds'
    ];
    return samples[rowIndex % samples.length];
  }

  if (colLower.includes('company') || colLower.includes('brand') || colLower.includes('vendor')) {
    const companies = ['Acme Corp', 'Global Tech Inc', 'Nexus Logistics', 'Apex Retail', 'Zenith Supplies'];
    return companies[rowIndex % companies.length];
  }

  return `${colName.replace(/_/g, ' ')} ${num}`;
}
